/**
 * `useLiveReplay(length)` — steps a cursor through a recorded live feed so the
 * telemetry tab can play the shift back as if it were arriving.
 *
 * The demo drew `B.live` as one static chart; the parity plan asks for the
 * feed to replay. This is that replay, and it is **deterministic**: a fixed
 * interval advancing a fixed number of samples per tick. No clock, no
 * randomness — the same frames in the same order every time.
 *
 * ## When it does not move
 *
 * The cursor's resting value is the **last frame** (the whole shift), which is
 * what renders:
 *
 * - on the server and in the parity snapshot renderer — effects never run
 *   there, so the static last frame is the output;
 * - when the reader has `prefers-reduced-motion: reduce` — the replay is not
 *   started, and the Play control is still there for anyone who wants it.
 *
 * Otherwise the replay starts from the first sample when the feed mounts. The
 * reset to frame one happens in a layout effect, before the browser paints, so
 * the full chart never flashes first.
 *
 * UI state for one component; lives beside the tab that uses it.
 */

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

/** Samples revealed per tick. 240 samples at 2 per 100 ms is a 12 s replay. */
const SAMPLES_PER_TICK = 2;
/** Tick interval, ms. */
const TICK_MS = 100;

/** `useLayoutEffect` in the browser, `useEffect` on the server (no warning). */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface LiveReplay {
  /** Samples revealed so far, 1…length. `length` is the static last frame. */
  frame: number;
  playing: boolean;
  /** Play from where it paused, or from the start if it has finished. */
  play: () => void;
  pause: () => void;
  /** Stop and show the whole shift. */
  showAll: () => void;
}

export function useLiveReplay(length: number): LiveReplay {
  const [frame, setFrame] = useState(length);
  const [playing, setPlaying] = useState(false);

  // Start once per feed, unless the reader has asked for less motion.
  useIsomorphicLayoutEffect(() => {
    setFrame(length);
    setPlaying(false);
    if (length < 2 || prefersReducedMotion()) return;
    setFrame(1);
    setPlaying(true);
  }, [length]);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setFrame((f) => Math.min(length, f + SAMPLES_PER_TICK));
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [playing, length]);

  // Stop at the end of the shift.
  useEffect(() => {
    if (playing && frame >= length) setPlaying(false);
  }, [playing, frame, length]);

  const play = useCallback(() => {
    setFrame((f) => (f >= length ? 1 : f));
    setPlaying(true);
  }, [length]);
  const pause = useCallback(() => setPlaying(false), []);
  const showAll = useCallback(() => {
    setPlaying(false);
    setFrame(length);
  }, [length]);

  return { frame, playing, play, pause, showAll };
}
